/**
 * Audio waveform downsampling utilities
 * Based on Flutter implementation for consistent visualization
 */

/**
 * Downsample audio data to target point count using peak detection
 * Divides data into buckets and takes the peak value from each bucket
 *
 * @param rawData - Original frequency/amplitude data
 * @param targetPointCount - Number of points to downsample to (default: 100)
 * @returns Downsampled array with exactly targetPointCount elements
 */
export const downsample = (rawData: Uint8Array, targetPointCount = 100): number[] => {
  const len = rawData.length;

  // If data is already smaller than target, return as is
  if (len === 0 || len <= targetPointCount) {
    return Array.from(rawData);
  }

  const sampledData: number[] = new Array(targetPointCount).fill(0);
  const bucketSize = len / targetPointCount;

  for (let i = 0; i < targetPointCount; i++) {
    const start = Math.floor(i * bucketSize);
    const end = Math.min(Math.floor((i + 1) * bucketSize), len);

    // Handle edge case where bucket is empty
    if (start >= end) {
      sampledData[i] = rawData[Math.max(0, Math.min(start, len - 1))];
      continue;
    }

    // Find peak value in this bucket
    let peak = rawData[start];
    for (let j = start + 1; j < end; j++) {
      if (rawData[j] > peak) {
        peak = rawData[j];
      }
    }
    sampledData[i] = peak;
  }

  return sampledData;
};
